
import { defineComponent, computed, ref, reactive, toRaw, watch, onMounted, watchEffect, getCurrentInstance } from 'vue';
import { message } from 'ant-design-vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import moment, { Moment } from 'moment';
import { settleTree, formatWithIntl, noEmptyProp, not } from '@/utils/illuminate';
import { createApiUrl } from '@/utils/utils';
import { request } from '@/utils/request';
import { getHoupiaoStore, HOUPIAO_FORM_KEY } from '@/views/fapiao/parts/utils';
import { EnterpriseInputItem } from '@/views/parts/subsidiary';
import routeTab from './parts/routeTab.vue';
import chargeList from './parts/chargeList.vue';

// 1 企业共享支付 2 企业个人账户余额 3 三方支付（支付宝）4 三方支付（微信）5 优惠券
export default defineComponent({
  name: 'IssueHoupiaoListView',
  components: { routeTab, chargeList },
  setup(props, ctx) {
    const visible = ref(false);
    const chargeOrderRef = ref();
    const instance = getCurrentInstance()?.proxy;
    const router = useRouter();
    const store = useStore();
    // 白名单客户”开发票“页面去掉开发票功能，替换成统一提示文案。
    const invoiceOption = reactive<{
      offlineInvoicing: boolean, // 是否可开票 true 线下开票 false 线上开票
      name: string | any,
      phone: string | any,
    }>({
      offlineInvoicing: false,
      name: '',
      phone: '',
    });
    const ifOfflineInvoicing = async () => {
      let res: any;
      try {
        res = await request.get(createApiUrl('/newlinkSass/invoice/offline-invoicing'), {});
        invoiceOption.offlineInvoicing = res.data.offlineInvoicing;
        invoiceOption.name = res.data.name;
        invoiceOption.phone = res.data.phone;
      } catch (e: Error & any) {
        message.error(e.message);
        throw e;
      }
    };
    ifOfflineInvoicing();
    const isOverActionTip = ref<boolean>(false);
    const isChargeOverActionTip = ref<boolean>(false);
    const filterParams = reactive<{
      usercode: string | undefined,
      vehicleId: string | undefined,
      isAll: boolean;
      dateRange: Moment[],
      payTimeRange: Moment[],
      enterpriseId: number | string,
      departmentId: number | undefined,
      enterprise: EnterpriseInputItem,
      payType: number | string | undefined,
    }>({
      usercode: undefined,
      vehicleId: undefined,
      isAll: false,
      dateRange: [],
      payTimeRange: [],
      enterpriseId: store.state.User.enterprise.enterpriseId,
      departmentId: undefined,
      enterprise: store.state.User.enterprise,
      payType: '',
    });

    /* = ----------------------------员工 = */
    const userList = ref<Array<Record<string, any>>>([]);
    // 获取员工列表
    const handleUserSearch = async (val: string) => {
      if (val) {
        const params = {
          nameOrPhone: val,
          enterpriseId: filterParams.enterpriseId, 
          status: 'ALL',
        };
        request.get<Record<string, string>[]>(createApiUrl('/newlinkSass/enterprise/find-user-list-by-name-or-phone'), { params }).then((res: any) => {
          const { data, code } = res;
          if (code !== 200) return message.error(res.message);
          userList.value = data;
        });
      }
    }; 
    
    /* = ----------------------------车辆 --------------------------------= */
    const vehicleList = ref<Array<Record<string, string>>>([]);
    // 模糊搜索
    const handleVehicleSearch = (value: any) => {
      if (value) {
        const params = { licensePlateOrVin: value, enterpriseId: filterParams.enterpriseId };
        request.get(createApiUrl('/newlinkSass/vehicle/fuzzySearchVehicle'), { params }).then((res: any) => {
          const { data, code } = res;
          if (code !== 200) return message.error(res.message);
          vehicleList.value = data;
        });
      }
    };

    /* = ---------------------------- = 统计 = ---------------------------- = */
    const statistics = ref({ orderNum: 0, totalAmount: 0, eleOrderNum: 0, eleOrderAmount: 0, overtimeOrderNum: 0, overtimeOrderAmount: 0 });

    /* = ---------------------------- = 支付方式选单 = ---------------------------- = */
    const payTypeList = ref<{ code: number | string; name: string; }[]>([{ code: '', name: '全部' }]);
    const loadPayTypeList = async () => {
      try {
        const res: any = await request.get(createApiUrl('/newlinkSass/invoice/get-invoice-pay-type-list'));
        payTypeList.value = [{ code: '', name: '全部' }, ...(res.data || [])];
      } catch (e: Error & any) {
        message.error(e.message);
      }
    };

    const actionTipVisible = computed({
      get(this: any): boolean {
        return !statistics.value.orderNum && isOverActionTip.value;
      },
      set(this: any, val: boolean) {
        isOverActionTip.value = val;
      },
    });
    const chargeTipVisible = computed({
      get(this: any): boolean {
        return !statistics.value.orderNum && isChargeOverActionTip.value;
      },
      set(this: any, val: boolean) {
        isChargeOverActionTip.value = val;
      },
    });

    const invoiceOrderStatistics = async () => {
      const params: any = {
        ...noEmptyProp(not(filterParams, ['dateRange', 'payTimeRange', 'enterprise', 'statementBillNo'])),
        enterpriseId: filterParams.enterprise.enterpriseId,
        departmentId: filterParams.departmentId,
      };

      if (filterParams.dateRange.length) {
        params.startTime = filterParams.dateRange[0].valueOf();
        params.endTime = filterParams.dateRange[1].valueOf();
      }

      if (filterParams.payTimeRange.length) {
        params.payStartTime = filterParams.payTimeRange[0].valueOf();
        params.payEndTime = filterParams.payTimeRange[1].valueOf();
      }

      // if (filterParams.statementBillNo) {
      //   params.statementBillNoList = [filterParams.statementBillNo];
      // }

      let res: any;
      try {
        res = await request.post(createApiUrl('/newlinkSass/invoice/find-invoice-ele-statisticsV2'), params);
      } catch (e: Error & any) {
        message.error(e.message);
        throw e;
      }

      statistics.value = res.data || { orderNum: 0, totalAmount: 0, eleOrderNum: 0, eleOrderAmount: 0, overtimeOrderNum: 0, overtimeOrderAmount: 0 };
    };

    // 点击全选按钮
    watch(filterParams, val => {
      if (val.isAll && filterParams.dateRange.length) {
        filterParams.dateRange = [];
        return;
      }

      if ((val.isAll && !val.dateRange.length) || (!val.isAll && (val.dateRange.length || val.payTimeRange.length))) {
        // onRefreshTable();
        invoiceOrderStatistics();
      } else {
        statistics.value = { orderNum: 0, totalAmount: 0, eleOrderNum: 0, eleOrderAmount: 0, overtimeOrderNum: 0, overtimeOrderAmount: 0 };
      }
    });

    const onGotoForm = () => {
      if (!statistics.value.orderNum) return message.error('请筛选开票订单!');
      const storage = getHoupiaoStore();

      const params: any = { ...not(filterParams, ['dateRange', 'payTimeRange']) };

      if (filterParams.dateRange.length) {
        params.startTime = filterParams.dateRange[0].valueOf();
        params.endTime = filterParams.dateRange[1].valueOf();
      }

      if (filterParams.payTimeRange.length) {
        params.payStartTime = filterParams.payTimeRange[0].valueOf();
        params.payEndTime = filterParams.payTimeRange[1].valueOf();
      }

      // if (filterParams.statementBillNo) {
      //   params.statementBillNoList = [filterParams.statementBillNo];
      // }
      
      storage.set(HOUPIAO_FORM_KEY, params);
      router.push({ name: 'fapiao.issue_houpiao.form', query: { type: 'charge' } });
    };

    onMounted(() => {
      console.log('onMounted');
      loadPayTypeList();
    });
    /* = -------------------------------------------------- = 加载搜索用的部门树 = -------------------------------------------------- = */
    const departmentList = ref<any[]>([]);
    const loadEnterpriseDepartment = async (val:any) => {
      filterParams.departmentId = undefined; // 清空部门
      filterParams.usercode = undefined;
      filterParams.vehicleId = undefined;
      vehicleList.value = [];
      userList.value = [];
      // 公司查询全部 部门为空
      if (val === '') {
        departmentList.value = [];
        return;
      }
      let res: any;
      try {
        res = await request.get(createApiUrl('/newlinkSass/enterprise/find-enterprise-department'), {
          params: {
            enterpriseId: val,
          },
        });
      } catch (e: Error & any) {
        return message.error(e.message);
      }

      departmentList.value = settleTree(res.data.departmentList,
        { key: 'id', title: 'departmentName', value: 'id' },
        'departmentList');
    };
    watchEffect(() => {
      loadEnterpriseDepartment(filterParams.enterpriseId);
    });
    const handleOrder = () => {
      if (!statistics.value.orderNum) return message.error('请筛选开票订单!');
      // if (!props.filterParams.dateRange.length && !props.filterParams.isAll) return { list: [] };

      const params: any = {
        ...noEmptyProp(not(filterParams, ['dateRange', 'payTimeRange', 'enterprise'])),
        enterpriseId: filterParams.enterprise.enterpriseId,
        departmentId: filterParams.departmentId,
      };
      if (filterParams.dateRange.length) {
        params.startTime = filterParams.dateRange[0].valueOf();
        params.endTime = filterParams.dateRange[1].valueOf();
      }

      if (filterParams.payTimeRange.length) {
        params.payStartTime = filterParams.payTimeRange[0].valueOf();
        params.payEndTime = filterParams.payTimeRange[1].valueOf();
      }
      // visible.value = true;
      if (chargeOrderRef.value) chargeOrderRef.value.show(params);
    };
    const onChangeDate = (val: Moment[]) => {
      if (!val || !val.length) {
        filterParams.dateRange = [];
        return;
      }
      filterParams.dateRange = [moment(val[0].format('YYYY-MM-DD HH:mm:ss')), moment(val[1].format('YYYY-MM-DD HH:mm:ss'))];
    };
    return {
      invoiceOption,
      statistics,
      filterParams,
      departmentList,
      isOverActionTip,
      isChargeOverActionTip,
      actionTipVisible,
      chargeTipVisible,

      onGotoForm,

      vehicleList,
      payTypeList,
      handleVehicleSearch,
      userList,
      handleUserSearch,
      handleOrder,
      visible,
      chargeOrderRef,
      onChangeDate,
      moment,
    };
  },
});
